import { Dialog } from '@hai/ui-react';
import { ButtonStateType } from '@hai/ui-react/dist/types';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props<T extends object> {
  title: string;
  titleInProgress?: string;
  itemsToDelete: T[];
  onDelete: (deletedItems: T[]) => Promise<any>;
  onSuccess?: (deletedItems: T[]) => void;
  onCancel?: VoidFunction;
}

export const GenericDeleteModal = <T extends object>(props: Props<T>) => {
  const { title, titleInProgress, itemsToDelete, onDelete, onSuccess, onCancel } = props;

  const { t } = useTranslation();
  const [buttonState, setButtonState] = useState<ButtonStateType>(undefined);
  const [cancelDisabled, setCancelDisabled] = useState(false);
  const [activeTitle, setActiveTitle] = useState(title);

  let endrun = false;
  useEffect(() => {
    return () => {
      endrun = true;
    };
  }, []);

  const handleDelete = (): any => {
    const response = onDelete(itemsToDelete);
    if (response != null && typeof response.then === 'function') {
      // we have a promise
      setButtonState('pending');
      setCancelDisabled(true);
      response.then((_res: any) => {
        setButtonState(undefined);
        onSuccess(itemsToDelete);
      });
      if (titleInProgress) {
        setTimeout(() => {
          if (endrun) {
            // ignore animation, we exited already
            return;
          }
          // switch display to progress
          if (titleInProgress) {
            setActiveTitle(titleInProgress);
          }
        }, 500); // to debounce flashing of messages before modal closes
      }
      return false; // to keep modal open
    }
  };

  const onKeyUp = (e: React.KeyboardEvent<HTMLElement>): void => {
    // console.log('key', e.key);
    if (e.key === 'Enter') { 
      if ((e.target as HTMLElement)?.id?.toString().includes('-submit')) {
        // from submit button, dont submit twice NKE-3307
        return;
      }
      const button = document.getElementById('delete-button-submit');
      if (button) {
        e.preventDefault();
        button.click();
      }
    }
  };

  useLayoutEffect((): void => {
    const el = document.getElementById('delete-button-submit');
    if (el) {
      el.focus();
    }
  }, []);

  return (
    <Dialog
      id="delete-modal"
      onKeyUp={onKeyUp}
      headline={t(activeTitle, { count: itemsToDelete.length })}
      bodyText={t('general.cannotUndo')}
      onClose={onCancel}
      show={true}
      buttons={[
        { label: t('general.cancel'), disabled: cancelDisabled },
        {
          label: t('general.deleteBtn'),
          variant: buttonState ? undefined : 'danger',
          onClick: handleDelete,
          state: buttonState,
          id: 'delete-button-submit',
        },
      ]}
    />
  );
};
